import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Server, CreditCard, Building2, Activity, BarChart3, LogOut, Shield, BrainCircuit,
  FileCheck, Boxes, Package, Handshake, ToggleRight, AlertTriangle, Sparkles,
  Eye, Coins, ClipboardList, Bell, Receipt, Users, Zap, FlaskConical, Wallet,
  Menu, X, Megaphone, ShieldCheck, Bug, Gauge, ListChecks, RotateCcw, ShieldAlert,
  Settings2, MailCheck,
  Palette,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAdminScope } from '@/hooks/useAdminScope';
import { useDirection } from '@/hooks/useDirection';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface NavItem {
  icon: React.ElementType;
  labelKey: string;
  path: string;
  moduleKey: string;
}

const navSections: { titleKey: string; items: NavItem[] }[] = [
  {
    titleKey: 'admin.nav.overview',
    items: [
      { icon: Shield, labelKey: 'admin.nav.dashboard', path: '/super-admin', moduleKey: 'dashboard' },
    ],
  },
  {
    titleKey: 'admin.nav.platform',
    items: [
      { icon: Server, labelKey: 'admin.nav.services', path: '/super-admin/services', moduleKey: 'services' },
      { icon: CreditCard, labelKey: 'admin.nav.plans', path: '/super-admin/plans', moduleKey: 'plans' },
      { icon: Package, labelKey: 'admin.nav.catalogSkus', path: '/super-admin/catalog', moduleKey: 'plans' },
      { icon: Boxes, labelKey: 'admin.nav.planServices', path: '/super-admin/plan-service-grid', moduleKey: 'plan-services' },

      { icon: Coins, labelKey: 'admin.nav.walletCreditBundles', path: '/super-admin/credit-packs', moduleKey: 'add-ons' },
      { icon: Settings2, labelKey: 'admin.nav.serviceCreditPricing', path: '/super-admin/service-credit-pricing', moduleKey: 'credits' },
      { icon: Handshake, labelKey: 'admin.nav.enterpriseDeals', path: '/super-admin/enterprise-deals', moduleKey: 'enterprise-deals' },
      { icon: ToggleRight, labelKey: 'admin.nav.featureFlags', path: '/super-admin/feature-flags', moduleKey: 'feature-flags' },
      { icon: Sparkles, labelKey: 'admin.nav.introPricing', path: '/super-admin/intro-pricing', moduleKey: 'plans' },
      { icon: FlaskConical, labelKey: 'admin.nav.trials', path: '/super-admin/trials', moduleKey: 'plans' },
    ],
  },
  {
    titleKey: 'admin.nav.operations',
    items: [
      { icon: Building2, labelKey: 'admin.nav.organizations', path: '/super-admin/organizations', moduleKey: 'organizations' },
      { icon: Users, labelKey: 'admin.nav.candidates', path: '/super-admin/candidates', moduleKey: 'candidates' },
      { icon: FileCheck, labelKey: 'admin.nav.identityRequests', path: '/super-admin/identity-requests', moduleKey: 'identity-requests' },
      { icon: Activity, labelKey: 'admin.nav.aiUsageLogs', path: '/super-admin/ai-usage', moduleKey: 'ai-usage' },
      { icon: BrainCircuit, labelKey: 'admin.nav.aiPrompts', path: '/super-admin/ai-prompts', moduleKey: 'ai-prompts' },
      { icon: Palette, labelKey: 'admin.nav.cvDesignLibrary', path: '/super-admin/cv-design-library', moduleKey: 'cv-design-library' },
      { icon: MailCheck, labelKey: 'admin.nav.emailTemplates', path: '/super-admin/email-templates', moduleKey: 'email-templates' },
      { icon: Zap, labelKey: 'admin.nav.aiFailures', path: '/super-admin/ai-failures', moduleKey: 'ai-failures' },
      { icon: BarChart3, labelKey: 'admin.nav.consumption', path: '/super-admin/consumption-overview', moduleKey: 'consumption-overview' },
      { icon: Coins, labelKey: 'admin.nav.wallets', path: '/super-admin/credits', moduleKey: 'credits' },
      { icon: Receipt, labelKey: 'admin.nav.subscriptions', path: '/super-admin/subscriptions', moduleKey: 'subscriptions' },
      { icon: FileCheck, labelKey: 'admin.nav.invoices', path: '/super-admin/invoices', moduleKey: 'invoices' },
      { icon: Wallet, labelKey: 'admin.nav.financeTools', path: '/super-admin/finance-tools', moduleKey: 'finance-tools' },
      { icon: RotateCcw, labelKey: 'admin.nav.paymentRecovery', path: '/super-admin/payment-recovery', moduleKey: 'payment-recovery' },
      { icon: Receipt, labelKey: 'admin.nav.refunds', path: '/super-admin/refunds', moduleKey: 'refunds' },
      { icon: AlertTriangle, labelKey: 'admin.nav.stalePending', path: '/super-admin/stale-pending', moduleKey: 'stale-pending' },
    ],
  },
  {
    titleKey: 'admin.nav.riskSupport',
    items: [
      { icon: AlertTriangle, labelKey: 'admin.nav.systemIssues', path: '/super-admin/system-issues', moduleKey: 'system-issues' },
      { icon: Bug, labelKey: 'admin.nav.errorEvents', path: '/super-admin/error-events', moduleKey: 'error-events' },
      { icon: ListChecks, labelKey: 'admin.nav.testIssues', path: '/super-admin/test-issues', moduleKey: 'test-issues' },
      { icon: Gauge, labelKey: 'admin.nav.governanceHealth', path: '/super-admin/governance-health', moduleKey: 'governance-health' },
      { icon: ShieldAlert, labelKey: 'admin.nav.governanceInspect', path: '/super-admin/governance-inspect', moduleKey: 'governance-inspect' },
      { icon: ShieldAlert, labelKey: 'admin.nav.deletionReviews', path: '/super-admin/deletion-reviews', moduleKey: 'governance-ops' },
      { icon: Eye, labelKey: 'admin.nav.shadowAccess', path: '/super-admin/shadow-access', moduleKey: 'shadow-access' },
      { icon: Bell, labelKey: 'admin.nav.alerts', path: '/super-admin/alerts', moduleKey: 'alerts' },
      { icon: Megaphone, labelKey: 'admin.nav.banners', path: '/super-admin/banners', moduleKey: 'banners' },
      { icon: ClipboardList, labelKey: 'admin.nav.actionLog', path: '/super-admin/action-log', moduleKey: 'action-log' },
      { icon: ShieldCheck, labelKey: 'admin.nav.governanceOps', path: '/super-admin/governance-ops', moduleKey: 'governance-ops' },
    ],
  },
  {
    titleKey: 'admin.nav.devTools',
    items: [
      { icon: FlaskConical, labelKey: 'admin.nav.paymentSimulator', path: '/super-admin/payment-simulator', moduleKey: 'payment-simulator' },
    ],
  },
];

export function SuperAdminLayout() {
  const location = useLocation();
  const { signOut } = useAdminAuth();
  const { canAccess, isLoading: scopeLoading } = useAdminScope();
  const { dir } = useDirection();
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const sidebarContent = (
    <>
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
        <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
          <Shield className="w-5 h-5 text-destructive" />
        </div>
        <span className="font-semibold text-base">{t('admin.brand')}</span>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} className="ms-auto p-1.5 rounded-lg hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
        {scopeLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          navSections.map((section) => {
            const visibleItems = section.items.filter((item) => canAccess(item.moduleKey));
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.titleKey}>
                <p className="px-3 mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t(section.titleKey)}
                </p>
                <div className="space-y-0.5">
                  {visibleItems.map((item) => {
                    const isActive =
                      item.path === '/super-admin'
                        ? location.pathname === '/super-admin'
                        : location.pathname.startsWith(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => isMobile && setMobileOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        {t(item.labelKey)}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </nav>

      <div className="p-3 border-t border-border space-y-1">
        <LanguageSwitcher variant="sidebar" />
        <button
          onClick={signOut}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted w-full"
          aria-label={t('admin.signOut')}
          data-testid="admin-sign-out-button"
        >
          <LogOut className="w-4 h-4" />
          {t('admin.signOut')}
        </button>
      </div>
    </>
  );

  return (
    <div dir={dir} className="flex h-dvh overflow-hidden bg-muted/30">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-dvh w-64 flex-col border-e border-border bg-card md:flex 3xl:w-72">
        {sidebarContent}
      </aside>

      {/* Mobile overlay sidebar */}
      {isMobile && mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 start-0 z-50 flex w-72 flex-col bg-card shadow-xl">
            {sidebarContent}
          </aside>
        </>
      )}

      {/* Main content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Mobile header */}
        <header className="h-14 flex items-center gap-3 border-b border-border px-4 bg-card/80 backdrop-blur-sm sticky top-0 z-30 md:hidden">
          <button onClick={() => setMobileOpen(true)} className="p-2 -ms-2 rounded-lg hover:bg-muted">
            <Menu className="w-5 h-5" />
          </button>
          <Shield className="w-5 h-5 text-destructive" />
          <span className="font-semibold text-sm">{t('admin.brand')}</span>
        </header>

        <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-6 py-5 safe-area-inset xs:px-7 sm:px-8 sm:py-6 md:p-8 lg:p-10 3xl:p-12 4xl:p-14">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
